import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Home,
  LogOut,
  Menu,
  X,
  Search,
  Filter,
  Eye,
  Ban,
  RefreshCw,
  Play,
  ChevronLeft,
  ChevronRight,
  Users,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Shield,
  Activity,
  Pause,
  DollarSign,
  FileWarning,
  Paperclip,
  Handshake,
  UserCheck,
  UserX,
  Ticket,
  Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle } from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const contractStatusConfig = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-700", icon: FileText },
  api_pending: { label: "En attente API", color: "bg-blue-100 text-blue-700", icon: Clock },
  active: { label: "Actif", color: "bg-green-100 text-green-700", icon: CheckCircle },
  suspended: { label: "Suspendu", color: "bg-orange-100 text-orange-700", icon: Pause },
  expired: { label: "Expiré", color: "bg-gray-100 text-gray-500", icon: XCircle },
  cancelled: { label: "Annulé", color: "bg-red-100 text-red-700", icon: XCircle },
  review_required: { label: "Révision requise", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  blocked: { label: "Bloqué", color: "bg-red-100 text-red-700", icon: Ban } };

const suspensionReasons = [
  { value: "suspicion_fraude", label: "Suspicion de fraude" },
  { value: "incoherence_donnees", label: "Incohérence de données" },
  { value: "incident_paiement", label: "Incident de paiement" },
  { value: "demande_assureur", label: "Demande de l'assureur" },
  { value: "autre", label: "Autre" },
];

const claimStatusConfig = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-700", icon: FileText },
  open: { label: "En cours", color: "bg-blue-100 text-blue-700", icon: Clock },
  refund: { label: "Remboursé", color: "bg-green-100 text-green-700", icon: DollarSign },
  refund_repeal: { label: "Rétractation", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  uncomplete: { label: "Incomplet", color: "bg-orange-100 text-orange-700", icon: FileWarning },
  refused: { label: "Refusé", color: "bg-red-100 text-red-700", icon: XCircle },
  closed: { label: "Clôturé", color: "bg-slate-100 text-slate-600", icon: CheckCircle } };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractsLoading, setContractsLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Modals
  const [suspendModal, setSuspendModal] = useState({ open: false, contract: null });
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendNote, setSuspendNote] = useState("");
  const [suspending, setSuspending] = useState(false);

  const [detailModal, setDetailModal] = useState({ open: false, contract: null, logs: [] });
  const [logsLoading, setLogsLoading] = useState(false);

  const [retryingId, setRetryingId] = useState(null);
  const [reactivatingId, setReactivatingId] = useState(null);

  // Tab
  const [activeTab, setActiveTab] = useState("contracts");

  // Claims
  const [claimsStats, setClaimsStats] = useState(null);
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimStatusFilter, setClaimStatusFilter] = useState("all");
  const [claimSearch, setClaimSearch] = useState("");
  const [syncingAll, setSyncingAll] = useState(false);
  const [claimDetailModal, setClaimDetailModal] = useState({ open: false, claim: null });

  // Affiliates
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [affiliatesLoading, setAffiliatesLoading] = useState(false);
  const [affiliateStatusFilter, setAffiliateStatusFilter] = useState("all");
  const [adminCommissions, setAdminCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [affiliateModal, setAffiliateModal] = useState({ open: false, affiliate: null, commissions: [], loading: false });
  const [activatingId, setActivatingId] = useState(null);
  const [suspendingAffId, setSuspendingAffId] = useState(null);

  // Migration
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResults, setEmailResults] = useState(null);

  // Promo codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: "",
    name: "",
    discount_type: "percent",
    percent_off: 10,
    amount_off: 10,
    duration: "once",
    max_redemptions: "",
    expires_at: "",
    first_time_transaction: false,
  });
  const [promoTogglingId, setPromoTogglingId] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      console.error("Stats error:", err);
    }
  }, []);

  const fetchContracts = useCallback(async () => {
    setContractsLoading(true);
    try {
      const params = { skip: page * pageSize, limit: pageSize };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const { data } = await axios.get(`${API_URL}/api/admin/contracts`, {
        params,
        withCredentials: true });
      setContracts(data);
    } catch (err) {
      console.error("Contracts error:", err);
      toast.error("Erreur lors du chargement des contrats");
    } finally {
      setContractsLoading(false);
    }
  }, [page, statusFilter, searchQuery]);

  const fetchClaimsStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/claims/stats`, { withCredentials: true });
      setClaimsStats(data);
    } catch (err) {
      console.error("Claims stats error:", err);
    }
  }, []);

  const fetchClaims = useCallback(async () => {
    setClaimsLoading(true);
    try {
      const params = {};
      if (claimStatusFilter && claimStatusFilter !== "all") params.status = claimStatusFilter;
      if (claimSearch) params.search = claimSearch;
      const { data } = await axios.get(`${API_URL}/api/admin/claims`, { params, withCredentials: true });
      setClaims(data);
    } catch (err) {
      console.error("Claims error:", err);
    } finally {
      setClaimsLoading(false);
    }
  }, [claimStatusFilter, claimSearch]);

  const fetchAffiliateStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/affiliates/stats`, { withCredentials: true });
      setAffiliateStats(data);
    } catch (err) {
      console.error("Affiliate stats error:", err);
    }
  }, []);

  const fetchAffiliates = useCallback(async () => {
    setAffiliatesLoading(true);
    try {
      const params = {};
      if (affiliateStatusFilter && affiliateStatusFilter !== "all") params.status = affiliateStatusFilter;
      const { data } = await axios.get(`${API_URL}/api/admin/affiliates`, { params, withCredentials: true });
      setAffiliates(data);
    } catch (err) {
      console.error("Affiliates error:", err);
    } finally {
      setAffiliatesLoading(false);
    }
  }, [affiliateStatusFilter]);

  const fetchAdminCommissions = useCallback(async () => {
    setCommissionsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/commissions`, { withCredentials: true });
      setAdminCommissions(data);
    } catch (err) {
      console.error("Commissions error:", err);
    } finally {
      setCommissionsLoading(false);
    }
  }, []);

  const fetchPromoCodes = useCallback(async () => {
    setPromoLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/promo-codes`, { withCredentials: true });
      setPromoCodes(data.data || []);
    } catch (err) {
      console.error("Promo codes error:", err);
    } finally {
      setPromoLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchContracts(), fetchClaimsStats(), fetchClaims(), fetchAffiliateStats(), fetchAffiliates(), fetchAdminCommissions(), fetchPromoCodes()]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchContracts, fetchClaimsStats, fetchClaims, fetchAffiliateStats, fetchAffiliates, fetchAdminCommissions, fetchPromoCodes]);

  const handleCreatePromo = async () => {
    if (!promoForm.code || !promoForm.name) {
      toast.error("Code et nom requis");
      return;
    }
    setPromoSubmitting(true);
    try {
      const payload = {
        code: promoForm.code.trim().toUpperCase(),
        name: promoForm.name.trim(),
        duration: promoForm.duration,
        first_time_transaction: !!promoForm.first_time_transaction,
      };
      if (promoForm.discount_type === "percent") {
        payload.percent_off = parseFloat(promoForm.percent_off);
      } else {
        payload.amount_off = parseFloat(promoForm.amount_off);
        payload.currency = "eur";
      }
      if (promoForm.max_redemptions) payload.max_redemptions = parseInt(promoForm.max_redemptions, 10);
      if (promoForm.expires_at) payload.expires_at = new Date(promoForm.expires_at + "T23:59:59Z").toISOString();
      await axios.post(`${API_URL}/api/admin/promo-codes`, payload, { withCredentials: true });
      toast.success(`Code ${payload.code} créé`);
      setPromoModalOpen(false);
      setPromoForm({ code: "", name: "", discount_type: "percent", percent_off: 10, amount_off: 10, duration: "once", max_redemptions: "", expires_at: "", first_time_transaction: false });
      await fetchPromoCodes();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Erreur création");
    } finally {
      setPromoSubmitting(false);
    }
  };

  const togglePromo = async (promo) => {
    setPromoTogglingId(promo.id);
    try {
      const action = promo.active ? "disable" : "enable";
      await axios.post(`${API_URL}/api/admin/promo-codes/${promo.id}/${action}`, {}, { withCredentials: true });
      toast.success(`Code ${promo.code} ${promo.active ? "désactivé" : "activé"}`);
      await fetchPromoCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setPromoTogglingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSuspend = async () => {
    if (!suspendReason) {
      toast.error("Veuillez sélectionner une raison");
      return;
    }
    setSuspending(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/contracts/${suspendModal.contract.id}/suspend`,
        { reason: suspendReason, internal_note: suspendNote },
        { withCredentials: true }
      );
      toast.success("Contrat suspendu avec succès");
      setSuspendModal({ open: false, contract: null });
      setSuspendReason("");
      setSuspendNote("");
      fetchStats();
      fetchContracts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suspension");
    } finally {
      setSuspending(false);
    }
  };

  const handleReactivate = async (contractId) => {
    setReactivatingId(contractId);
    try {
      await axios.post(
        `${API_URL}/api/admin/contracts/${contractId}/reactivate`,
        {},
        { withCredentials: true }
      );
      toast.success("Contrat réactivé");
      fetchStats();
      fetchContracts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la réactivation");
    } finally {
      setReactivatingId(null);
    }
  };

  const handleRetry = async (contractId) => {
    setRetryingId(contractId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/contracts/${contractId}/retry`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message || "Retry lancé");
      fetchStats();
      fetchContracts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du retry");
    } finally {
      setRetryingId(null);
    }
  };

  const handleSyncAllClaims = async () => {
    setSyncingAll(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/claims/sync-all`, {}, { withCredentials: true });
      toast.success(data.message);
      await Promise.all([fetchClaimsStats(), fetchClaims()]);
    } catch (err) {
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncingAll(false);
    }
  };

  const handleActivateAffiliate = async (id) => {
    setActivatingId(id);
    try {
      await axios.post(`${API_URL}/api/admin/affiliates/${id}/activate`, {}, { withCredentials: true });
      toast.success("Affilié activé");
      await Promise.all([fetchAffiliateStats(), fetchAffiliates()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setActivatingId(null);
    }
  };

  const handleSuspendAffiliate = async (id) => {
    setSuspendingAffId(id);
    try {
      await axios.post(`${API_URL}/api/admin/affiliates/${id}/suspend`, {}, { withCredentials: true });
      toast.success("Affilié suspendu");
      await Promise.all([fetchAffiliateStats(), fetchAffiliates()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSuspendingAffId(null);
    }
  };

  const refreshAffiliateModal = async () => {
    if (!affiliateModal.open || !affiliateModal.affiliate) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/commissions`, {
        params: { affiliate_id: affiliateModal.affiliate.id, limit: 200 },
        withCredentials: true,
      });
      setAffiliateModal((p) => ({ ...p, commissions: data }));
    } catch (_) {}
  };

  const openAffiliateCommissions = async (affiliate) => {
    setAffiliateModal({ open: true, affiliate, commissions: [], loading: true });
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/commissions`, {
        params: { affiliate_id: affiliate.id, limit: 200 },
        withCredentials: true,
      });
      setAffiliateModal((p) => ({ ...p, commissions: data, loading: false }));
    } catch (err) {
      toast.error("Erreur lors du chargement des commissions");
      setAffiliateModal((p) => ({ ...p, loading: false }));
    }
  };

  const handleValidateCommission = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/commissions/${id}/validate`, {}, { withCredentials: true });
      toast.success("Commission validée");
      fetchAdminCommissions();
      fetchAffiliates();
      refreshAffiliateModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/commissions/${id}/mark-paid`, {}, { withCredentials: true });
      toast.success("Commission marquée payée");
      fetchAdminCommissions();
      fetchAffiliates();
      refreshAffiliateModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleMonthlyProcess = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/commissions/monthly-process`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchAdminCommissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const openDetail = async (contract) => {
    setDetailModal({ open: true, contract, logs: [] });
    setLogsLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/contracts/${contract.id}/logs`,
        { withCredentials: true }
      );
      setDetailModal((prev) => ({ ...prev, logs: data }));
    } catch (err) {
      console.error("Logs error:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit" });
  };

  const statCards = stats
    ? [
        { label: "Total contrats", value: stats.total_contracts, icon: FileText, bg: "bg-slate-100", iconColor: "text-slate-600" },
        { label: "Actifs", value: stats.active_contracts, icon: CheckCircle, bg: "bg-green-100", iconColor: "text-green-600" },
        { label: "En attente", value: stats.pending_contracts, icon: Clock, bg: "bg-blue-100", iconColor: "text-blue-600" },
        { label: "Révision requise", value: stats.review_required, icon: AlertTriangle, bg: "bg-amber-100", iconColor: "text-amber-600" },
        { label: "Suspendus", value: stats.suspended_contracts, icon: Pause, bg: "bg-orange-100", iconColor: "text-orange-600" },
        { label: "Utilisateurs", value: stats.total_users, icon: Users, bg: "bg-indigo-100", iconColor: "text-indigo-600" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-2">
            <Link
              to="/admin"
              className="flex items-center gap-3 px-4 py-3 text-[#19294C] bg-slate-100 rounded-lg font-medium"
              data-testid="nav-admin-dashboard"
            >
              <Shield className="w-5 h-5" />
              Administration
            </Link>
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg"
              data-testid="nav-user-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Tableau de bord
            </Link>
            <Link
              to="/logements"
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg"
            >
              <Home className="w-5 h-5" />
              Logements
            </Link>
          </nav>
          <div className="p-4 border-t border-slate-100">
            <div className="px-4 py-3 mb-2">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">
                Admin
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg w-full text-left"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 hover:text-slate-900">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#19294C]" />
              <h1 className="text-xl font-semibold text-[#19294C] font-['Outfit']">Administration</h1>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-500" />
              <span className="text-sm text-slate-500">Système opérationnel</span>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8" data-testid="admin-dashboard-content">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00B8E4]" />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="admin-stats-grid">
                {statCards.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-9 h-9 ${s.bg} rounded-lg flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 ${s.iconColor}`} />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                      <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Tab Navigation */}
              <div className="mb-6 -mx-4 sm:mx-0 overflow-x-auto">
                <div className="flex gap-1 bg-white rounded-lg border border-slate-200 shadow-sm p-1 w-max mx-4 sm:mx-0 sm:w-fit" data-testid="admin-tabs">
                  <button
                    onClick={() => setActiveTab("contracts")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === "contracts" ? "bg-[#19294C] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid="admin-tab-contracts"
                  >
                    Contrats {stats ? `(${stats.total_contracts})` : ""}
                  </button>
                  <button
                    onClick={() => setActiveTab("claims")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === "claims" ? "bg-[#19294C] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid="admin-tab-claims"
                  >
                    Sinistres {claimsStats ? `(${claimsStats.total_claims})` : ""}
                  </button>
                  <button
                    onClick={() => setActiveTab("affiliates")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === "affiliates" ? "bg-[#19294C] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid="admin-tab-affiliates"
                  >
                    Affiliés {affiliateStats ? `(${affiliateStats.total_affiliates})` : ""}
                  </button>
                  <button
                    onClick={() => setActiveTab("promo")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === "promo" ? "bg-[#19294C] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid="admin-tab-promo"
                  >
                    Codes promo {promoCodes.length > 0 ? `(${promoCodes.length})` : ""}
                  </button>
                  <button
                    onClick={() => setActiveTab("migration")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === "migration" ? "bg-[#19294C] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid="admin-tab-migration"
                  >
                    Migration
                  </button>
                </div>
              </div>

              {activeTab === "contracts" && (
              <>
              {/* Contracts Section */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">Contrats</h2>
                </div>

                {/* Filters */}
                <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3" data-testid="admin-filters">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher par email, nom, référence..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(0);
                      }}
                      data-testid="admin-search-input"
                    />
                  </div>
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => {
                      setStatusFilter(v);
                      setPage(0);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-status-filter">
                      <Filter className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="active">Actif</SelectItem>
                      <SelectItem value="api_pending">En attente API</SelectItem>
                      <SelectItem value="review_required">Révision requise</SelectItem>
                      <SelectItem value="suspended">Suspendu</SelectItem>
                      <SelectItem value="blocked">Bloqué</SelectItem>
                      <SelectItem value="cancelled">Annulé</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => {
                      fetchStats();
                      fetchContracts();
                    }}
                    data-testid="admin-refresh-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </div>

                {/* Table */}
                {contractsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00B8E4]" />
                  </div>
                ) : contracts.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucun contrat trouvé</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-contracts-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Utilisateur</TableHead>
                          <TableHead>Logement</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Référence</TableHead>
                          <TableHead>Créé le</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contracts.map((c) => {
                          const cfg = contractStatusConfig[c.status] || contractStatusConfig.draft;
                          const StatusIcon = cfg.icon;
                          return (
                            <TableRow key={c.id} data-testid={`admin-contract-row-${c.id}`}>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{c.user_name}</p>
                                  <p className="text-xs text-slate-500">{c.user_email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{c.property_name}</p>
                                  <p className="text-xs text-slate-500 truncate max-w-[200px]">{c.property_address}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {cfg.label}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-600 font-mono">
                                  {c.meetch_reference || c.id.slice(0, 8)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-500">{formatDate(c.created_at)}</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openDetail(c)}
                                    data-testid={`admin-view-btn-${c.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>

                                  {c.status === "active" && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                      onClick={() => setSuspendModal({ open: true, contract: c })}
                                      data-testid={`admin-suspend-btn-${c.id}`}
                                    >
                                      <Ban className="w-4 h-4" />
                                    </Button>
                                  )}

                                  {c.status === "suspended" && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => handleReactivate(c.id)}
                                      disabled={reactivatingId === c.id}
                                      data-testid={`admin-reactivate-btn-${c.id}`}
                                    >
                                      {reactivatingId === c.id ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent" />
                                      ) : (
                                        <Play className="w-4 h-4" />
                                      )}
                                    </Button>
                                  )}

                                  {c.status === "review_required" && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      onClick={() => handleRetry(c.id)}
                                      disabled={retryingId === c.id}
                                      data-testid={`admin-retry-btn-${c.id}`}
                                    >
                                      {retryingId === c.id ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                                      ) : (
                                        <RefreshCw className="w-4 h-4" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Pagination */}
                {contracts.length > 0 && (
                  <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Page {page + 1} ({contracts.length} résultats)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => setPage((p) => p - 1)}
                        data-testid="admin-prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={contracts.length < pageSize}
                        onClick={() => setPage((p) => p + 1)}
                        data-testid="admin-next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              </>
              )}

              {activeTab === "claims" && (
              <>
              {/* Claims Stats */}
              {claimsStats && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="admin-claims-stats">
                  {[
                    { label: "Total", value: claimsStats.total_claims, bg: "bg-slate-100", iconColor: "text-slate-600", icon: FileText },
                    { label: "En cours", value: claimsStats.open_claims, bg: "bg-blue-100", iconColor: "text-blue-600", icon: Clock },
                    { label: "Remboursés", value: claimsStats.refunded_claims, bg: "bg-green-100", iconColor: "text-green-600", icon: DollarSign },
                    { label: "Refusés", value: claimsStats.refused_claims, bg: "bg-red-100", iconColor: "text-red-600", icon: XCircle },
                    { label: "Incomplets", value: claimsStats.uncomplete_claims, bg: "bg-orange-100", iconColor: "text-orange-600", icon: FileWarning },
                    { label: "Clôturés", value: claimsStats.closed_claims, bg: "bg-slate-100", iconColor: "text-slate-500", icon: CheckCircle },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-9 h-9 ${s.bg} rounded-lg flex items-center justify-center`}>
                            <Icon className={`w-4 h-4 ${s.iconColor}`} />
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                        <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Claims Table */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">Sinistres</h2>
                </div>
                <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3" data-testid="admin-claims-filters">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher par email, nom, logement..."
                      className="pl-10"
                      value={claimSearch}
                      onChange={(e) => setClaimSearch(e.target.value)}
                      data-testid="admin-claims-search"
                    />
                  </div>
                  <Select value={claimStatusFilter} onValueChange={setClaimStatusFilter}>
                    <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-claims-status-filter">
                      <Filter className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="draft">Brouillon</SelectItem>
                      <SelectItem value="open">En cours</SelectItem>
                      <SelectItem value="refund">Remboursé</SelectItem>
                      <SelectItem value="uncomplete">Incomplet</SelectItem>
                      <SelectItem value="refused">Refusé</SelectItem>
                      <SelectItem value="closed">Clôturé</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={handleSyncAllClaims} disabled={syncingAll} data-testid="admin-sync-all-claims">
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingAll ? "animate-spin" : ""}`} />
                    Synchroniser tout
                  </Button>
                </div>

                {claimsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00B8E4]" />
                  </div>
                ) : claims.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucun sinistre trouvé</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-claims-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Utilisateur</TableHead>
                          <TableHead>Logement</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Ref. Meetch</TableHead>
                          <TableHead>Date incident</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {claims.map((c) => {
                          const cfg = claimStatusConfig[c.meetch_status] || claimStatusConfig.draft;
                          const StatusIcon = cfg.icon;
                          return (
                            <TableRow key={c.id} data-testid={`admin-claim-row-${c.id}`}>
                              <TableCell>
                                <p className="font-medium text-slate-900 text-sm">{c.user_name}</p>
                                <p className="text-xs text-slate-500">{c.user_email}</p>
                              </TableCell>
                              <TableCell>
                                <p className="font-medium text-slate-900 text-sm">{c.property_name}</p>
                                <p className="text-xs text-slate-500 truncate max-w-[180px]">{c.property_address}</p>
                              </TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {c.status_label || cfg.label}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-600 font-mono">{c.meetch_claim_id || "-"}</span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-500">{formatDate(c.issue_date)}</span>
                              </TableCell>
                              <TableCell>
                                {c.refund_amount > 0 ? (
                                  <span className="text-sm font-medium text-green-600">{c.refund_amount?.toFixed(2)}€</span>
                                ) : (
                                  <span className="text-sm text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button variant="ghost" size="sm" onClick={() => setClaimDetailModal({ open: true, claim: c })} data-testid={`admin-claim-view-${c.id}`}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              </>
              )}

              {activeTab === "affiliates" && (
              <>
              {/* Affiliate Stats */}
              {affiliateStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="admin-affiliate-stats">
                  {[
                    { label: "Total", value: affiliateStats.total_affiliates, bg: "bg-slate-100", ic: "text-slate-600", icon: Users },
                    { label: "Actifs", value: affiliateStats.active_affiliates, bg: "bg-green-100", ic: "text-green-600", icon: CheckCircle },
                    { label: "En attente", value: affiliateStats.pending_affiliates, bg: "bg-amber-100", ic: "text-amber-600", icon: Clock },
                    { label: "Commissions", value: `${affiliateStats.total_commissions_amount?.toFixed(2)}€`, bg: "bg-indigo-100", ic: "text-indigo-600", icon: DollarSign },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                        <div className={`w-9 h-9 ${s.bg} rounded-lg flex items-center justify-center mb-2`}>
                          <Icon className={`w-4 h-4 ${s.ic}`} />
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                        <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Affiliates Table */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">Affiliés</h2>
                  <Select value={affiliateStatusFilter} onValueChange={(v) => { setAffiliateStatusFilter(v); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Tous" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="active">Actifs</SelectItem>
                      <SelectItem value="suspended">Suspendus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {affiliatesLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00B8E4]" />
                  </div>
                ) : affiliates.length === 0 ? (
                  <div className="p-12 text-center">
                    <Handshake className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucun affilié</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-affiliates-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Partenaire</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Clics</TableHead>
                          <TableHead>Parrainés</TableHead>
                          <TableHead>Commissions</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {affiliates.map((a) => (
                          <TableRow key={a.id} data-testid={`admin-affiliate-row-${a.id}`}>
                            <TableCell>
                              <p className="font-medium text-slate-900 text-sm">{a.user_name}</p>
                              <p className="text-xs text-slate-500">{a.user_email}</p>
                            </TableCell>
                            <TableCell><span className="font-mono text-sm text-slate-600">{a.affiliate_code}</span></TableCell>
                            <TableCell>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                a.status === "active" ? "bg-green-100 text-green-700" :
                                a.status === "pending" ? "bg-amber-100 text-amber-700" :
                                "bg-red-100 text-red-700"
                              }`}>
                                {a.status === "active" ? "Actif" : a.status === "pending" ? "En attente" : "Suspendu"}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{a.total_clicks}</TableCell>
                            <TableCell className="text-sm text-slate-600">{a.total_referrals}</TableCell>
                            <TableCell className="text-sm font-medium text-slate-900">{a.total_commissions?.toFixed(2)}€</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" className="text-slate-600 hover:bg-slate-100" onClick={() => openAffiliateCommissions(a)} title="Voir les commissions" data-testid={`admin-view-affiliate-${a.id}`}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {a.status === "pending" && (
                                  <Button variant="ghost" size="sm" className="text-green-600 hover:bg-green-50" onClick={() => handleActivateAffiliate(a.id)} disabled={activatingId === a.id} data-testid={`admin-activate-affiliate-${a.id}`}>
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                )}
                                {a.status === "active" && (
                                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleSuspendAffiliate(a.id)} disabled={suspendingAffId === a.id} data-testid={`admin-suspend-affiliate-${a.id}`}>
                                    <UserX className="w-4 h-4" />
                                  </Button>
                                )}
                                {a.status === "suspended" && (
                                  <Button variant="ghost" size="sm" className="text-green-600 hover:bg-green-50" onClick={() => handleActivateAffiliate(a.id)} disabled={activatingId === a.id}>
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Commissions Table */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">Commissions</h2>
                  <Button variant="outline" size="sm" onClick={handleMonthlyProcess} data-testid="admin-monthly-process-btn">
                    <DollarSign className="w-4 h-4 mr-2" /> Process mensuel
                  </Button>
                </div>
                {commissionsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00B8E4]" />
                  </div>
                ) : adminCommissions.length === 0 ? (
                  <div className="p-8 text-center">
                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucune commission</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-commissions-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Montant</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Fraude</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adminCommissions.map((c) => (
                          <TableRow key={c.id} data-testid={`admin-commission-row-${c.id}`}>
                            <TableCell className="text-sm text-slate-600">{formatDate(c.created_at)}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.type === "initial" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                                {c.type === "initial" ? "Initial" : "Renouvellement"}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">{c.amount?.toFixed(2)}€</TableCell>
                            <TableCell className="text-sm font-medium text-green-600">{c.commission_amount?.toFixed(2)}€</TableCell>
                            <TableCell>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                c.status === "paid" ? "bg-green-100 text-green-700" :
                                c.status === "validated" ? "bg-blue-100 text-blue-700" :
                                c.status === "to_be_invoiced" ? "bg-indigo-100 text-indigo-700" :
                                c.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                                "bg-red-100 text-red-700"
                              }`}>
                                {c.status === "paid" ? "Payée" : c.status === "validated" ? "Validée" : c.status === "to_be_invoiced" ? "À facturer" : c.status === "pending" ? "En attente" : "Annulée"}
                              </span>
                            </TableCell>
                            <TableCell>
                              {c.fraud_flag && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium">Flag</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {c.status === "pending" && (
                                  <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => handleValidateCommission(c.id)} data-testid={`admin-validate-comm-${c.id}`}>
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                {(c.status === "validated" || c.status === "to_be_invoiced") && (
                                  <Button variant="ghost" size="sm" className="text-green-600" onClick={() => handleMarkPaid(c.id)} data-testid={`admin-markpaid-comm-${c.id}`}>
                                    <DollarSign className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              </>
              )}
            </>
          )}

              {activeTab === "migration" && (
                <div className="space-y-6" data-testid="migration-tab">
                  {/* Migration Status */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-[#19294C] mb-4">Statut de la migration</h3>
                    <button
                      onClick={async () => {
                        setMigrationLoading(true);
                        try {
                          const { data } = await axios.get(`${API_URL}/api/admin/migration/status`, { withCredentials: true });
                          setMigrationStatus(data);
                        } catch (err) { toast.error("Erreur chargement statut"); }
                        setMigrationLoading(false);
                      }}
                      className="bg-[#19294C] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#19294C]/90 transition-colors"
                      disabled={migrationLoading}
                      data-testid="migration-check-status"
                    >
                      {migrationLoading ? "Chargement..." : "Vérifier le statut"}
                    </button>
                    {migrationStatus && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-[#19294C]">{migrationStatus.migrated_users}</p>
                          <p className="text-xs text-slate-500 mt-1">Clients migrés</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-[#19294C]">{migrationStatus.migrated_properties}</p>
                          <p className="text-xs text-slate-500 mt-1">Propriétés migrées</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-[#19294C]">{migrationStatus.migrated_transactions}</p>
                          <p className="text-xs text-slate-500 mt-1">Transactions archivées</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className={`text-2xl font-bold ${migrationStatus.migration_done ? "text-emerald-600" : "text-orange-500"}`}>
                            {migrationStatus.migration_done ? "OK" : "Non"}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">Migration active</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Send Welcome Emails */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-[#19294C] mb-2">Envoyer les emails de bienvenue</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Envoie un email à tous les utilisateurs migrés avec leurs identifiants de connexion
                      (mot de passe temporaire : <strong>HostProtect2026!</strong>).
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={async () => {
                          if (!window.confirm("Envoyer un email de bienvenue à tous les utilisateurs migrés ? Cette action est irréversible.")) return;
                          setSendingEmails(true);
                          setEmailResults(null);
                          try {
                            const { data } = await axios.post(`${API_URL}/api/admin/migration/send-welcome-emails`,
                              { temp_password: "HostProtect2026!", site_url: window.location.origin },
                              { withCredentials: true }
                            );
                            setEmailResults(data);
                            toast.success(`${data.sent} emails envoyés !`);
                          } catch (err) {
                            toast.error("Erreur lors de l'envoi");
                          }
                          setSendingEmails(false);
                        }}
                        className="bg-[#00B8E4] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#00a5ce] transition-colors disabled:opacity-50"
                        disabled={sendingEmails}
                        data-testid="migration-send-emails"
                      >
                        {sendingEmails ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Envoi en cours...
                          </span>
                        ) : (
                          "Envoyer les emails de bienvenue"
                        )}
                      </button>
                    </div>
                    {emailResults && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm"><span className="font-semibold text-emerald-600">{emailResults.sent}</span> emails envoyés</p>
                        {emailResults.failed > 0 && (
                          <p className="text-sm text-red-500 mt-1">{emailResults.failed} échecs : {emailResults.errors?.join(", ")}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Rollback */}
                  <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-red-600 mb-2">Zone de danger</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Annuler complètement la migration : supprime tous les utilisateurs, propriétés et transactions importés.
                    </p>
                    <button
                      onClick={async () => {
                        if (!window.confirm("ATTENTION : Cela supprimera TOUTES les données migrées. Êtes-vous sûr ?")) return;
                        if (!window.confirm("Dernière confirmation : cette action est irréversible.")) return;
                        try {
                          const { data } = await axios.post(`${API_URL}/api/admin/migration/rollback`, {}, { withCredentials: true });
                          toast.success(`Rollback effectué : ${data.results.users_removed} users, ${data.results.properties_removed} propriétés supprimées`);
                          setMigrationStatus(null);
                        } catch (err) { toast.error("Erreur rollback"); }
                      }}
                      className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600 transition-colors"
                      data-testid="migration-rollback"
                    >
                      Annuler la migration (rollback)
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "promo" && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="promo-tab">
                  <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ticket className="w-5 h-5 text-[#00B8E4]" />
                      <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">Codes promo Stripe</h2>
                    </div>
                    <Button
                      onClick={() => setPromoModalOpen(true)}
                      className="bg-[#00B8E4] hover:bg-[#00A0CC] text-white"
                      data-testid="promo-create-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Créer un code
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Code</TableHead>
                          <TableHead>Nom</TableHead>
                          <TableHead>Remise</TableHead>
                          <TableHead>Durée</TableHead>
                          <TableHead>Utilisations</TableHead>
                          <TableHead>Expire</TableHead>
                          <TableHead>Restrictions</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {promoLoading ? (
                          <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Chargement...</TableCell></TableRow>
                        ) : promoCodes.length === 0 ? (
                          <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Aucun code promo. Cliquez sur "Créer un code" pour commencer.</TableCell></TableRow>
                        ) : (
                          promoCodes.map((p) => {
                            const c = p.coupon || {};
                            const remise = c.percent_off ? `-${c.percent_off}%` : c.amount_off ? `-${c.amount_off.toFixed(2)}€` : "?";
                            const used = `${p.times_redeemed}/${p.max_redemptions || "∞"}`;
                            const exp = p.expires_at ? new Date(p.expires_at * 1000).toLocaleDateString("fr-FR") : "—";
                            const rest = p.restrictions || {};
                            return (
                              <TableRow key={p.id} data-testid={`promo-row-${p.code}`}>
                                <TableCell className="font-mono font-semibold">{p.code}</TableCell>
                                <TableCell className="text-slate-600">{c.name || "—"}</TableCell>
                                <TableCell className="font-semibold text-[#19294C]">{remise}</TableCell>
                                <TableCell>{c.duration === "once" ? "Une fois" : c.duration === "forever" ? "Toujours" : c.duration_in_months ? `${c.duration_in_months} mois` : "—"}</TableCell>
                                <TableCell>{used}</TableCell>
                                <TableCell className="text-sm text-slate-500">{exp}</TableCell>
                                <TableCell className="text-xs text-slate-500">
                                  {rest.first_time_transaction && <span className="block">1er achat</span>}
                                  {rest.minimum_amount && <span className="block">min {(rest.minimum_amount / 100).toFixed(2)}€</span>}
                                  {!rest.first_time_transaction && !rest.minimum_amount && "—"}
                                </TableCell>
                                <TableCell>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                                    {p.active ? "Actif" : "Désactivé"}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => togglePromo(p)}
                                    disabled={promoTogglingId === p.id}
                                    data-testid={`promo-toggle-${p.code}`}
                                  >
                                    {promoTogglingId === p.id ? "..." : p.active ? "Désactiver" : "Activer"}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
        </main>
      </div>

      {/* Suspend Modal */}
      <Dialog
        open={suspendModal.open}
        onOpenChange={(open) => {
          if (!open) {
            setSuspendModal({ open: false, contract: null });
            setSuspendReason("");
            setSuspendNote("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="suspend-modal">
          <DialogHeader>
            <DialogTitle className="text-[#19294C] font-['Outfit']">Suspendre le contrat</DialogTitle>
            <DialogDescription>
              {suspendModal.contract && (
                <>
                  Contrat pour <strong>{suspendModal.contract.property_name}</strong> ({suspendModal.contract.user_name})
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Raison de la suspension *
              </label>
              <Select value={suspendReason} onValueChange={setSuspendReason}>
                <SelectTrigger data-testid="suspend-reason-select">
                  <SelectValue placeholder="Sélectionner une raison" />
                </SelectTrigger>
                <SelectContent>
                  {suspensionReasons.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Note interne (optionnelle)
              </label>
              <Textarea
                placeholder="Ajouter un commentaire interne..."
                value={suspendNote}
                onChange={(e) => setSuspendNote(e.target.value)}
                rows={3}
                data-testid="suspend-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSuspendModal({ open: false, contract: null })}
            >
              Annuler
            </Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleSuspend}
              disabled={suspending || !suspendReason}
              data-testid="confirm-suspend-btn"
            >
              {suspending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Suspension...
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4 mr-2" />
                  Confirmer la suspension
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog
        open={detailModal.open}
        onOpenChange={(open) => {
          if (!open) setDetailModal({ open: false, contract: null, logs: [] });
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="detail-modal">
          <DialogHeader>
            <DialogTitle className="text-[#19294C] font-['Outfit']">Détails du contrat</DialogTitle>
          </DialogHeader>
          {detailModal.contract && (
            <div className="space-y-6 py-2">
              {/* Contract Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Utilisateur</p>
                  <p className="text-sm font-medium">{detailModal.contract.user_name}</p>
                  <p className="text-xs text-slate-400">{detailModal.contract.user_email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Logement</p>
                  <p className="text-sm font-medium">{detailModal.contract.property_name}</p>
                  <p className="text-xs text-slate-400">{detailModal.contract.property_address}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Statut</p>
                  {(() => {
                    const cfg = contractStatusConfig[detailModal.contract.status] || contractStatusConfig.draft;
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    );
                  })()}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Référence Meetch</p>
                  <p className="text-sm font-mono">{detailModal.contract.meetch_reference || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Créé le</p>
                  <p className="text-sm">{formatDate(detailModal.contract.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Activé le</p>
                  <p className="text-sm">{formatDate(detailModal.contract.activated_at)}</p>
                </div>
                {detailModal.contract.retry_count > 0 && (
                  <div>
                    <p className="text-xs text-slate-500">Tentatives</p>
                    <p className="text-sm">{detailModal.contract.retry_count}</p>
                  </div>
                )}
                {detailModal.contract.last_error && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Dernière erreur</p>
                    <p className="text-sm text-red-600 font-mono bg-red-50 px-3 py-2 rounded-lg mt-1">
                      {detailModal.contract.last_error}
                    </p>
                  </div>
                )}
                {detailModal.contract.suspension_reason && (
                  <div className="col-span-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-xs text-orange-600 font-medium mb-1">Suspension</p>
                    <p className="text-sm">
                      <strong>Raison :</strong>{" "}
                      {suspensionReasons.find((r) => r.value === detailModal.contract.suspension_reason)?.label ||
                        detailModal.contract.suspension_reason}
                    </p>
                    {detailModal.contract.suspension_note && (
                      <p className="text-sm mt-1">
                        <strong>Note :</strong> {detailModal.contract.suspension_note}
                      </p>
                    )}
                    <p className="text-xs text-orange-400 mt-1">
                      Suspendu le {formatDate(detailModal.contract.suspended_at)}
                    </p>
                  </div>
                )}
              </div>

              {/* API Logs */}
              <div>
                <h4 className="text-sm font-semibold text-[#19294C] mb-3">Logs API Meetch</h4>
                {logsLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#00B8E4]" />
                  </div>
                ) : detailModal.logs.length === 0 ? (
                  <p className="text-sm text-slate-400">Aucun log disponible</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {detailModal.logs.map((log, i) => (
                      <div
                        key={log.id || i}
                        className={`text-xs p-3 rounded-lg border ${
                          log.response_status === 200
                            ? "bg-green-50 border-green-200"
                            : "bg-red-50 border-red-200"
                        }`}
                      >
                        <div className="flex justify-between mb-1">
                          <span className="font-medium">
                            Tentative #{log.attempt_number} — HTTP {log.response_status}
                          </span>
                          <span className="text-slate-400">{formatDate(log.created_at)}</span>
                        </div>
                        {log.error_message && (
                          <p className="text-red-600 mt-1">{log.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Claim Detail Modal */}
      <Dialog
        open={claimDetailModal.open}
        onOpenChange={(open) => {
          if (!open) setClaimDetailModal({ open: false, claim: null });
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="claim-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-[#19294C] font-['Outfit']">Détails du sinistre</DialogTitle>
          </DialogHeader>
          {claimDetailModal.claim && (() => {
            const c = claimDetailModal.claim;
            const cfg = claimStatusConfig[c.meetch_status] || claimStatusConfig.draft;
            return (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Utilisateur</p>
                    <p className="text-sm font-medium">{c.user_name}</p>
                    <p className="text-xs text-slate-400">{c.user_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Logement</p>
                    <p className="text-sm font-medium">{c.property_name}</p>
                    <p className="text-xs text-slate-400">{c.property_address}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Statut Meetch</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                      {c.status_label || cfg.label}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Ref. Meetch</p>
                    <p className="text-sm font-mono">{c.meetch_claim_id || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Ref. contrat</p>
                    <p className="text-sm font-mono">{c.meetch_reference || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Date incident</p>
                    <p className="text-sm">{formatDate(c.issue_date)}</p>
                  </div>
                  {c.requested_amount > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Montant demandé</p>
                      <p className="text-sm">{c.requested_amount?.toFixed(2)}€</p>
                    </div>
                  )}
                  {c.refund_amount > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Montant remboursé</p>
                      <p className="text-sm text-green-600 font-medium">{c.refund_amount?.toFixed(2)}€</p>
                    </div>
                  )}
                  {c.comment && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">Commentaire</p>
                      <p className="text-sm bg-slate-50 rounded p-2 mt-1">{c.comment}</p>
                    </div>
                  )}
                  {c.refusal_reason && (
                    <div className="col-span-2 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-xs text-red-600 font-medium">Motif de refus</p>
                      <p className="text-sm text-red-700 mt-1">{c.refusal_reason}</p>
                    </div>
                  )}
                </div>
                {c.attachments && c.attachments.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                      <Paperclip className="w-3.5 h-3.5" /> Pièces jointes ({c.attachments.length})
                    </p>
                    <div className="space-y-1">
                      {c.attachments.map((att, i) => (
                        <a key={att.id || i} href={att.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded border border-slate-200 hover:bg-slate-50 text-sm">
                          <FileText className="w-3.5 h-3.5 text-slate-400" />
                          <span className="truncate">{att.filename || "Document"}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Promo Code Create Modal */}
      <Dialog open={promoModalOpen} onOpenChange={setPromoModalOpen}>
        <DialogContent data-testid="promo-create-modal">
          <DialogHeader>
            <DialogTitle>Créer un code promo</DialogTitle>
            <DialogDescription>
              Le code sera créé sur Stripe (Coupon + Promotion Code) et utilisable immédiatement au checkout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Code (que tape le client) *</label>
              <Input
                placeholder="BIENVENUE20"
                value={promoForm.code}
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                data-testid="promo-code-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom interne *</label>
              <Input
                placeholder="Promotion lancement -20%"
                value={promoForm.name}
                onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
                data-testid="promo-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Type de remise *</label>
              <Select
                value={promoForm.discount_type}
                onValueChange={(v) => setPromoForm({ ...promoForm, discount_type: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Pourcentage</SelectItem>
                  <SelectItem value="amount">Montant fixe (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                {promoForm.discount_type === "percent" ? "Pourcentage de réduction (%)" : "Montant de réduction (€)"} *
              </label>
              <Input
                type="number"
                min="0.01"
                max={promoForm.discount_type === "percent" ? "100" : undefined}
                step="0.01"
                value={promoForm.discount_type === "percent" ? promoForm.percent_off : promoForm.amount_off}
                onChange={(e) => setPromoForm({
                  ...promoForm,
                  [promoForm.discount_type === "percent" ? "percent_off" : "amount_off"]: e.target.value,
                })}
                data-testid="promo-value-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Durée *</label>
              <Select value={promoForm.duration} onValueChange={(v) => setPromoForm({ ...promoForm, duration: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Une seule fois</SelectItem>
                  <SelectItem value="forever">Tous les renouvellements</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Utilisations max (optionnel)</label>
                <Input
                  type="number"
                  min="1"
                  placeholder="100"
                  value={promoForm.max_redemptions}
                  onChange={(e) => setPromoForm({ ...promoForm, max_redemptions: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date d'expiration (optionnel)</label>
                <Input
                  type="date"
                  value={promoForm.expires_at}
                  onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={promoForm.first_time_transaction}
                onChange={(e) => setPromoForm({ ...promoForm, first_time_transaction: e.target.checked })}
                className="rounded"
              />
              Premier achat uniquement
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoModalOpen(false)}>Annuler</Button>
            <Button
              onClick={handleCreatePromo}
              disabled={promoSubmitting}
              className="bg-[#00B8E4] hover:bg-[#00A0CC] text-white"
              data-testid="promo-submit-btn"
            >
              {promoSubmitting ? "Création..." : "Créer le code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Affiliate Commissions Modal */}
      <Dialog
        open={affiliateModal.open}
        onOpenChange={(open) => {
          if (!open) setAffiliateModal({ open: false, affiliate: null, commissions: [], loading: false });
        }}
      >
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="affiliate-commissions-modal">
          <DialogHeader>
            <DialogTitle className="text-[#19294C] font-['Outfit']">
              Commissions de {affiliateModal.affiliate?.user_name}
            </DialogTitle>
            <DialogDescription>
              <span className="text-slate-600">{affiliateModal.affiliate?.user_email}</span>
              {affiliateModal.affiliate?.affiliate_code && (
                <span className="ml-3 font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                  {affiliateModal.affiliate.affiliate_code}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {affiliateModal.loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00B8E4]" />
            </div>
          ) : (
            <>
              {/* Stats summary */}
              <div className="grid grid-cols-4 gap-3 my-4">
                {(() => {
                  const total = affiliateModal.commissions.reduce((s, c) => s + (c.commission_amount || 0), 0);
                  const pending = affiliateModal.commissions.filter((c) => c.status === "pending").reduce((s, c) => s + (c.commission_amount || 0), 0);
                  const validated = affiliateModal.commissions.filter((c) => ["validated", "to_be_invoiced"].includes(c.status)).reduce((s, c) => s + (c.commission_amount || 0), 0);
                  const paid = affiliateModal.commissions.filter((c) => c.status === "paid").reduce((s, c) => s + (c.commission_amount || 0), 0);
                  const cards = [
                    { label: "Total", value: total, bg: "bg-slate-100", color: "text-slate-700" },
                    { label: "En attente", value: pending, bg: "bg-yellow-50", color: "text-yellow-700" },
                    { label: "Validées", value: validated, bg: "bg-blue-50", color: "text-blue-700" },
                    { label: "Payées", value: paid, bg: "bg-green-50", color: "text-green-700" },
                  ];
                  return cards.map((c) => (
                    <div key={c.label} className={`${c.bg} rounded-lg p-3`}>
                      <p className="text-xs text-slate-500">{c.label}</p>
                      <p className={`text-lg font-semibold ${c.color}`}>{c.value.toFixed(2)}€</p>
                    </div>
                  ));
                })()}
              </div>

              {/* Commissions Table */}
              {affiliateModal.commissions.length === 0 ? (
                <div className="p-8 text-center">
                  <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucune commission pour cet affilié</p>
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <Table data-testid="affiliate-commissions-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Commission</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Fraude</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affiliateModal.commissions.map((c) => (
                        <TableRow key={c.id} data-testid={`affiliate-commission-row-${c.id}`}>
                          <TableCell className="text-sm text-slate-600">{formatDate(c.created_at)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.type === "initial" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                              {c.type === "initial" ? "Initial" : "Renouvellement"}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">{c.amount?.toFixed(2)}€</TableCell>
                          <TableCell className="text-sm font-medium text-green-600">{c.commission_amount?.toFixed(2)}€</TableCell>
                          <TableCell>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              c.status === "paid" ? "bg-green-100 text-green-700" :
                              c.status === "validated" ? "bg-blue-100 text-blue-700" :
                              c.status === "to_be_invoiced" ? "bg-indigo-100 text-indigo-700" :
                              c.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {c.status === "paid" ? "Payée" : c.status === "validated" ? "Validée" : c.status === "to_be_invoiced" ? "À facturer" : c.status === "pending" ? "En attente" : "Annulée"}
                            </span>
                          </TableCell>
                          <TableCell>
                            {c.fraud_flag && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium">Flag</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {c.status === "pending" && (
                                <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => handleValidateCommission(c.id)} data-testid={`affiliate-validate-comm-${c.id}`}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              {(c.status === "validated" || c.status === "to_be_invoiced") && (
                                <Button variant="ghost" size="sm" className="text-green-600" onClick={() => handleMarkPaid(c.id)} data-testid={`affiliate-markpaid-comm-${c.id}`}>
                                  <DollarSign className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
